import { observer } from 'mobx-react-lite';
import type { JSX, ReactElement } from 'react';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { Billable, IEvent, IEventBilling } from '@feathr/blackbox';
import type { Event } from '@feathr/blackbox';
import { Step, Steps } from '@feathr/components';
import BillableStepFour from '@feathr/extender/App/Settings/Billing/BillablePage/BillableAddPage/BillableStepFour';
import BillableStepOne from '@feathr/extender/App/Settings/Billing/BillablePage/BillableAddPage/BillableStepOne';
import BillableStepThree from '@feathr/extender/App/Settings/Billing/BillablePage/BillableAddPage/BillableStepThree';
import BillableStepTwo from '@feathr/extender/App/Settings/Billing/BillablePage/BillableAddPage/BillableStepTwo';
import { StoresContext, useLocalUrl } from '@feathr/extender/state';
import { logUserEvents, useToggle } from '@feathr/hooks';
import type { Model } from '@feathr/rachis';

import EventAddStepOne from './EventAddStepOne';
import EventAddStepThree from './EventAddStepThree';
import EventAddStepTwo from './EventAddStepTwo';

import * as styles from './EventAdd.css';

function EventAdd(): JSX.Element {
  const { Billables, Events } = useContext(StoresContext);
  const { t } = useTranslation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const [event] = useState(Events.create());
  const [billable] = useState(Billables.create());
  const [currentStep, setCurrentStep] = useState(0);
  const [addingBillable, toggleAddingBillable] = useToggle(false);

  const steps: ReactElement[] = [
    <Step key={0} stepIndex={0} stepNumber={'1'} title={t('Project information')} />,
    <Step key={1} stepIndex={1} stepNumber={'2'} title={t('Billing')} />,
    <Step
      key={addingBillable ? 6 : 2}
      stepIndex={addingBillable ? 6 : 2}
      stepNumber={'3'}
      title={t('Finish')}
    />,
  ];
  if (addingBillable) {
    steps.splice(
      2,
      0,
      <Step key={2} stepIndex={2} stepNumber={'2a'} title={t('Contact information')} />,
      <Step key={3} stepIndex={3} stepNumber={'2b'} title={t('Billing address')} />,
      <Step key={4} stepIndex={4} stepNumber={'2c'} title={t('Payment method')} />,
      <Step key={5} stepIndex={5} stepNumber={'2d'} title={t('Description')} />,
    );
  }

  function handleSaveEvent(savedEvent: Model<IEvent>): void {
    logUserEvents({ 'Created project': { eventId: savedEvent.id } });
    history.push(localUrl((savedEvent as Event).getItemUrl()));
  }

  function handleOnAdd(): void {
    toggleAddingBillable();
    setCurrentStep(currentStep + 1);
  }

  function handleOnNext(): void {
    setCurrentStep(currentStep + 1);
  }

  function handleOnPrevious(): void {
    setCurrentStep(currentStep - 1);
  }

  function handleOnSave(savedBillable: Billable): void {
    event.set({
      billing: {
        billable_id: savedBillable.get('id'),
      } as IEventBilling,
    });
    billable.set({ id: savedBillable.get('id') });
    billable.id = savedBillable.get('id')!;
    setCurrentStep(currentStep + 1);
  }

  return (
    <div className={styles.root}>
      <Steps className={styles.steps} current={currentStep}>
        {steps}
      </Steps>

      {currentStep === 0 && <EventAddStepOne event={event} onNext={handleOnNext} />}

      {currentStep === 1 && (
        <EventAddStepTwo
          event={event}
          onAdd={handleOnAdd}
          onNext={handleOnNext}
          onPrevious={handleOnPrevious}
        />
      )}

      {addingBillable && currentStep === 2 && (
        <BillableStepOne billable={billable} onNext={handleOnNext} onPrevious={handleOnPrevious} />
      )}

      {addingBillable && currentStep === 3 && (
        <BillableStepTwo billable={billable} onNext={handleOnNext} onPrevious={handleOnPrevious} />
      )}

      {addingBillable && currentStep === 4 && (
        <BillableStepThree
          billable={billable}
          onNext={handleOnNext}
          onPrevious={handleOnPrevious}
        />
      )}

      {addingBillable && currentStep === 5 && (
        <BillableStepFour
          billable={billable}
          onPrevious={handleOnPrevious}
          onSave={handleOnSave}
          saveButtonText={t('Next')}
        />
      )}

      {((addingBillable && currentStep === 6) || (!addingBillable && currentStep === 2)) && (
        <EventAddStepThree event={event} onPrevious={handleOnPrevious} onSave={handleSaveEvent} />
      )}
    </div>
  );
}

export default observer(EventAdd);
